/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react';
import {
  BlipType,
  useDataState,
  useRadarState,
  Utilities
} from '@undp_sdg_ai_lab/undp-radar';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { BlipsPerQuadType } from '../quadrant/QuadrantHorizonList';
import { HorizonItemMui } from '../quadrant/HorizonItemMui';

interface QuadType {
  qIndex: number;
  horizons: BlipsPerQuadType;
}

export const BlipListMui: React.FC = React.memo(() => {
  const {
    state: {
      blips,
      filteredBlips,
      isFiltered,
      radarData: { quadrants }
    }
  } = useRadarState();
  const {
    state: {
      keys: { horizonKey }
    }
  } = useDataState();

  const [expanded, setExpanded] = React.useState<string>('');

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : '');
    };

  const [displayBlips, setDisplayBlips] = useState<BlipType[]>([]);
  const [quadBlips, setQuadBlips] = useState<QuadType[]>([]);

  useEffect(() => {
    let blipsToUse = blips;
    if (isFiltered) {
      blipsToUse = filteredBlips;
    }
    setDisplayBlips(blipsToUse);
  }, [blips, filteredBlips]);

  useEffect(() => {
    const quads = new Array<QuadType>();
    for (let i = 0; i < quadrants.length; i++) {
      const q: QuadType = {
        qIndex: i,
        horizons: {}
      };
      quads.push(q);
    }
    displayBlips.forEach((blip) => {
      const q = quads[blip.quadrantIndex];
      const h = q.horizons;
      const hName: string = blip[horizonKey];
      if (h[hName] === undefined) {
        h[hName] = new Array<BlipType>();
      }
      h[hName].push(blip);
      setQuadBlips(quads);
    });
  }, [displayBlips]);

  const reorderQuadrants = (quadrants: string[]): string[] => {
    if (!quadrants.length) {
      return [];
    }
    const lastItem = quadrants.pop();
    quadrants.splice(0, 0, lastItem as string);
    return quadrants;
  };

  return (
    <>
      {reorderQuadrants([...quadrants]).map((quad) => (
        <Accordion
          key={quad}
          TransitionProps={{ unmountOnExit: true }}
          expanded={expanded === quad}
          onChange={handleChange(quad)}
          data-testid={`quadrant-section-${quad}`}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1bh-content'
            id={quad + '-header'}
          >
            <Typography sx={{ width: '33%', flexShrink: 0 }}>
              {Utilities.capitalize(quad)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Horizons quadrant={quad} blips={quadBlips} />
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
});

const Horizons: React.FC<{
  quadrant: string;
  blips: QuadType[];
}> = ({ quadrant, blips }) => {
  const {
    state: {
      radarData: { quadrants, horizons }
    }
  } = useRadarState();

  const [expandedHorizon, setExpandedHorizon] = React.useState<string>('');

  const handleChangeHorizon =
    (horizon: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedHorizon(isExpanded ? horizon : '');
    };

  const quadB = blips[quadrants.indexOf(quadrant)];
  return (
    <>
      {quadB &&
        [...horizons]
          .reverse()
          .map((hName: string) => (
            <HorizonItemMui
              key={quadrant + '-' + hName}
              horizonName={Utilities.capitalize(hName)}
              handleChange={handleChangeHorizon}
              quadrantBlips={quadB.horizons[hName]}
              expandedHorizon={expandedHorizon}
            />
          ))}
    </>
  );
};
